package er.prateek.crossly.ads

/**
 * Google's published sample ad units, which always fill and never earn -- the only ids that
 * are safe to build and test against.
 *
 * These stay compiled in as the fallback forever, not just during development:
 * [AdsConfig.testMode] or an empty override in [AdUnitOverrides] both resolve back to here,
 * so a mistyped console value degrades to test inventory rather than to a broken or
 * policy-violating real request.
 *
 * Sources: developers.google.com/admob/android/test-ads and .../ios/test-ads.
 */
object TestAdUnits {
    object Android {
        /** Goes in AndroidManifest's `com.google.android.gms.ads.APPLICATION_ID` meta-data. */
        const val APP_ID = "ca-app-pub-3940256099942544~3347511713"
        const val BANNER = "ca-app-pub-3940256099942544/6300978111"
        const val INTERSTITIAL = "ca-app-pub-3940256099942544/1033173712"
        const val REWARDED = "ca-app-pub-3940256099942544/5224354917"
    }

    object Ios {
        /** Goes in Info.plist's `GADApplicationIdentifier`. */
        const val APP_ID = "ca-app-pub-3940256099942544~1458002511"
        const val BANNER = "ca-app-pub-3940256099942544/2934735716"
        const val INTERSTITIAL = "ca-app-pub-3940256099942544/4411468910"
        const val REWARDED = "ca-app-pub-3940256099942544/1712485313"
    }

    /**
     * AdSense has no sample unit ids -- the equivalent is `data-adtest="on"` on the `<ins>`
     * tag, which the web host sets whenever [AdsConfig.testMode] is on. These placeholders
     * exist only so the tag is well-formed before a real publisher account is linked; with
     * `data-adtest="on"` set, no request is billed or counted against them.
     */
    object Web {
        const val CLIENT = "ca-pub-0000000000000000"
        const val BANNER_SLOT = "0000000000"
    }
}

/**
 * Resolves the id to actually request with: the live override when there is one and test
 * mode is off, the compiled test id otherwise.
 */
object AdUnitIds {
    private fun resolve(config: AdsConfig, override: String, testId: String): String =
        if (config.testMode || override.isBlank()) testId else override

    fun androidBanner(config: AdsConfig) =
        resolve(config, config.unitIds.androidBanner, TestAdUnits.Android.BANNER)

    fun androidInterstitial(config: AdsConfig) =
        resolve(config, config.unitIds.androidInterstitial, TestAdUnits.Android.INTERSTITIAL)

    fun androidRewarded(config: AdsConfig) =
        resolve(config, config.unitIds.androidRewarded, TestAdUnits.Android.REWARDED)

    fun iosBanner(config: AdsConfig) =
        resolve(config, config.unitIds.iosBanner, TestAdUnits.Ios.BANNER)

    fun iosInterstitial(config: AdsConfig) =
        resolve(config, config.unitIds.iosInterstitial, TestAdUnits.Ios.INTERSTITIAL)

    fun iosRewarded(config: AdsConfig) =
        resolve(config, config.unitIds.iosRewarded, TestAdUnits.Ios.REWARDED)

    fun webClient(config: AdsConfig) =
        resolve(config, config.unitIds.webClient, TestAdUnits.Web.CLIENT)

    fun webBannerSlot(config: AdsConfig) =
        resolve(config, config.unitIds.webBannerSlot, TestAdUnits.Web.BANNER_SLOT)
}
